import { HStack, Pressable, Text, VStack } from '@gluestack-ui/themed';
import { CheckIcon } from 'lucide-react-native';
import { useContext } from 'react';
import { TranslationContext } from '../../contexts/translations/Translation';
import { COLORS } from '../../theme/colors';

// The user's pre-guess ("verrou d'hypothèse"): they commit before the assisted
// result is revealed. Stored on CreateAnalysisDTO.properties.userHypothesis
// (free-form), so no API contract change is required. 'idk' = the student
// declined to commit (the "je ne sais pas" abstain mode).
export interface UserHypothesis {
  gram?: 'pos' | 'neg' | 'idk';
  shape?: 'cocci' | 'bacillus' | 'idk';
}

const AXES: { field: keyof UserHypothesis; labelKey: string; options: { key: string; labelKey: string }[] }[] = [
  { field: 'gram', labelKey: 'gram-label', options: [{ key: 'pos', labelKey: 'gram-pos' }, { key: 'neg', labelKey: 'gram-neg' }] },
  { field: 'shape', labelKey: 'shape-label', options: [{ key: 'cocci', labelKey: 'shape-cocci' }, { key: 'bacillus', labelKey: 'shape-bacillus' }] },
];

interface IHypothesisSectionProps {
  value: UserHypothesis;
  onChange: (next: UserHypothesis) => void;
  isDisabled?: boolean;
  // Methylene blue has no Gram reaction, so the Gram axis is hidden then.
  showGram?: boolean;
  testID?: string;
}

export const HypothesisSection = ({ value, onChange, isDisabled, showGram = true, testID }: IHypothesisSectionProps) => {
  const { i18n } = useContext(TranslationContext);
  const base = 'verrou.hypothesis';

  const axes = AXES.filter((a) => a.field !== 'gram' || showGram);
  const allIdk = axes.length > 0 && axes.every((a) => value[a.field] === 'idk');

  const setIdk = () => {
    if (isDisabled) return;
    const next = { ...value };
    axes.forEach((a) => { next[a.field] = 'idk'; });
    onChange(next);
  };

  return (
    <VStack gap={10}>
      <Text fontWeight="bold" fontSize={14}>
        {i18n?.t(`${base}.title`)}
      </Text>
      <Text fontSize={12} color={COLORS.NEUTRAL[90]}>
        {i18n?.t(`${base}.subtitle`)}
      </Text>

      {axes.map((axis) => (
        <VStack key={axis.field} gap={6}>
          <Text fontSize={12} color={COLORS.NEUTRAL[90]}>
            {i18n?.t(`${base}.${axis.labelKey}`)}
          </Text>
          <HStack gap={10} flexWrap="wrap">
            {axis.options.map((opt) => {
              const isSelected = value[axis.field] === opt.key;
              return (
                <Pressable
                  key={opt.key}
                  testID={`${testID ?? ''}hypo_${axis.field}_${opt.key}`}
                  onPress={() => !isDisabled && onChange({ ...value, [axis.field]: opt.key })}
                  style={{
                    flex: 1,
                    minWidth: 120,
                    borderWidth: 2,
                    borderColor: isSelected ? COLORS.ACCENT[100] : COLORS.NEUTRAL[70],
                    borderRadius: 10,
                    padding: 12,
                    backgroundColor: isSelected ? '#FFF5F4' : COLORS.NEUTRAL[0],
                    opacity: isDisabled ? 0.5 : 1,
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                  }}
                >
                  <HStack gap={8} alignItems="center" justifyContent="space-between">
                    <Text fontWeight="bold" fontSize={13}>
                      {i18n?.t(`${base}.${opt.labelKey}`)}
                    </Text>
                    {isSelected && <CheckIcon size={16} color={COLORS.ACCENT[100]} />}
                  </HStack>
                </Pressable>
              );
            })}
          </HStack>
        </VStack>
      ))}

      <Pressable
        testID={`${testID ?? ''}hypo_idk`}
        onPress={setIdk}
        style={{
          borderWidth: 1,
          borderStyle: 'dashed',
          borderColor: allIdk ? COLORS.ACCENT[100] : COLORS.NEUTRAL[70],
          borderRadius: 10,
          paddingVertical: 9,
          alignItems: 'center',
          backgroundColor: allIdk ? '#FFF5F4' : 'transparent',
          opacity: isDisabled ? 0.5 : 1,
          cursor: isDisabled ? 'not-allowed' : 'pointer',
        }}
      >
        <Text fontSize={12} color={allIdk ? COLORS.ACCENT[100] : COLORS.NEUTRAL[90]}>
          {i18n?.t(`${base}.idk`)}
        </Text>
      </Pressable>
    </VStack>
  );
};
